import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { GraphQLClient } from "graphql-request";
import { gql } from "graphql-request";
import { User, UserSyncResponse } from "../components/pricing-table/types";
import { USER_SYNC_MUTATION } from "../components/pricing-table/queries";
import { entryRefreshToken } from "../entryParams";

const LOGIN_OR_REGISTER_MUTATION = gql`
  mutation LoginOrRegister($input: LoginOrRegisterInput!) {
    loginOrRegister(input: $input) {
      boolean
    }
  }
`;

const GET_TOKEN_MUTATION = gql`
  mutation GetToken($input: TokenInput!) {
    token(input: $input) {
      authToken {
        token
        refreshToken
      }
    }
  }
`;

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  userData: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSynced: boolean;
}

interface AuthContextValue extends AuthState {
  loginOrRegister: (email: string) => Promise<boolean>;
  verifyCode: (code: string) => Promise<boolean>;
  loginWithRefreshToken: (refreshToken: string) => Promise<boolean>;
  logout: () => void;
  saveAuthToCookie: () => void;
  pendingEmail: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const GRAPHQL_ENDPOINT =
  process.env.REACT_APP_GRAPHQL_ENDPOINT || "http://localhost:5001/api/graphql";

const GRAPHQL_ANON_ENDPOINT =
  process.env.REACT_APP_GRAPHQL_ANONYMOUS_ENDPOINT ||
  "http://localhost:5001/api/graphql-anonymous";

function getAnonClient() {
  return new GraphQLClient(GRAPHQL_ANON_ENDPOINT, {
    headers: { "Content-Type": "application/json" },
  });
}

function getAuthClient(token: string) {
  return new GraphQLClient(GRAPHQL_ENDPOINT, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const hasSynced = useRef(false);

  const syncUser = useCallback(async (token: string) => {
    if (hasSynced.current) return;
    try {
      const client = getAuthClient(token);
      const data = await client.request<UserSyncResponse>(USER_SYNC_MUTATION);
      setUserData(data.userSync.user);
      hasSynced.current = true;
      setIsSynced(true);
    } catch (error) {
      console.error("userSync failed:", error);
    }
  }, []);

  const applyToken = useCallback(
    async (token: string, refresh: string) => {
      // Clear previous session before applying new one
      setAccessToken(null);
      setRefreshToken(null);
      setUserData(null);
      setIsSynced(false);
      hasSynced.current = false;

      setAccessToken(token);
      setRefreshToken(refresh);
      const maxAge = 60 * 60 * 24 * 30; // 30 days
      document.cookie = `gg_refresh_token=${encodeURIComponent(refresh)}; max-age=${maxAge}; path=/; SameSite=Strict`;
      await syncUser(token);
    },
    [syncUser],
  );

  const loginOrRegister = useCallback(
    async (email: string): Promise<boolean> => {
      const client = getAnonClient();
      const data = await client.request<{ loginOrRegister: { boolean: boolean } }>(
        LOGIN_OR_REGISTER_MUTATION,
        { input: { email } },
      );
      const success = data.loginOrRegister?.boolean ?? false;
      if (success) {
        setPendingEmail(email);
      }
      return success;
    },
    [],
  );

  const verifyCode = useCallback(
    async (code: string): Promise<boolean> => {
      try {
        const client = getAnonClient();
        const data = await client.request<{
          token: { authToken: { token: string; refreshToken: string } };
        }>(GET_TOKEN_MUTATION, {
          input: {
            input: { code },
          },
        });
        const { token, refreshToken: rt } = data.token.authToken;
        await applyToken(token, rt);
        setPendingEmail(null);
        return true;
      } catch {
        return false;
      }
    },
    [applyToken],
  );

  const loginWithRefreshToken = useCallback(
    async (rt: string): Promise<boolean> => {
      try {
        const client = getAnonClient();
        const data = await client.request<{
          token: { authToken: { token: string; refreshToken: string } };
        }>(GET_TOKEN_MUTATION, {
          input: {
            input: { refreshToken: rt },
          },
        });
        const { token, refreshToken: newRt } = data.token.authToken;
        await applyToken(token, newRt);
        return true;
      } catch {
        return false;
      }
    },
    [applyToken],
  );

  // Restore session on mount - URL param takes priority over cookie.
  // The token is read from entryParams, which captured it at module load and
  // already removed it from the address bar - before the router read the URL.
  useEffect(() => {
    const urlRt = entryRefreshToken;

    if (urlRt) {
      // Clear any stale cookie from a previous user before logging in with the new token
      document.cookie = "gg_refresh_token=; max-age=0; path=/";

      setIsLoading(true);
      (async () => {
        try {
          const client = getAnonClient();
          const data = await client.request<{
            token: { authToken: { token: string; refreshToken: string } };
          }>(GET_TOKEN_MUTATION, { input: { input: { refreshToken: urlRt } } });
          const { token, refreshToken: newRt } = data.token.authToken;
          await applyToken(token, newRt);
        } catch (e) {
          console.error("refreshtoken login failed:", e);
        } finally {
          setIsLoading(false);
        }
      })();
      return;
    }

    // No URL token - try cookie restore
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith("gg_refresh_token="));
    if (!match) return;
    const cookieRt = decodeURIComponent(match.split("=")[1]);
    setIsLoading(true);
    loginWithRefreshToken(cookieRt).finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const AUTH_COOKIE_NAME = "gg_refresh_token";

  const saveAuthToCookie = useCallback(() => {
    if (!refreshToken) return;
    const maxAge = 60 * 60 * 24 * 30; // 30 days
    document.cookie = `${AUTH_COOKIE_NAME}=${encodeURIComponent(refreshToken)}; max-age=${maxAge}; path=/; SameSite=Strict`;
  }, [refreshToken]);

  const logout = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setUserData(null);
    setIsSynced(false);
    hasSynced.current = false;
    setPendingEmail(null);
    document.cookie = `${AUTH_COOKIE_NAME}=; max-age=0; path=/`;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        refreshToken,
        userData,
        isAuthenticated: !!accessToken && isSynced,
        isLoading,
        isSynced,
        loginOrRegister,
        verifyCode,
        loginWithRefreshToken,
        logout,
        saveAuthToCookie,
        pendingEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider");
  return ctx;
};
